import { useEffect, useRef } from "react";

import { getChangedFields, isEqual } from "../../util";
import { sanitiseTableRowUpdate } from "./sanitiseTableRowUpdate";

const getMutationPayloadError = result => {
  const payload = Object.values(result?.data || {}).find(
    value =>
      value !== null &&
      typeof value === "object" &&
      Object.prototype.hasOwnProperty.call(value, "success"),
  );

  if (payload?.success !== false) {
    return null;
  }

  const error = payload?.error;
  if (error instanceof Error) {
    return error;
  }

  const message =
    (typeof error === "string" && error) ||
    error?.message ||
    "The update could not be saved";

  return new Error(message);
};

const processNextRowUpdate = async refs => {
  const {
    formikRef,
    headersRef,
    latestValuesRef,
    onMutationErrorRef,
    pendingValuesRef,
    rowRef,
    savedValuesRef,
    updateInProgressRef,
    updateMutationRef,
  } = refs;
  const submittedValues = pendingValuesRef.current;
  pendingValuesRef.current = null;

  if (!submittedValues) {
    updateInProgressRef.current = false;
    return;
  }

  const changedFields = getChangedFields(
    submittedValues,
    savedValuesRef.current,
  );
  const pruned = sanitiseTableRowUpdate(changedFields, headersRef.current);

  if (Object.keys(pruned).length > 0 && updateMutationRef.current) {
    try {
      const result = await updateMutationRef.current({
        variables: {
          input: {
            id: submittedValues?.id,
            ...pruned,
          },
        },
      });
      const payloadError = getMutationPayloadError(result);

      if (payloadError) {
        onMutationErrorRef.current?.(payloadError);
      } else {
        savedValuesRef.current = submittedValues;
      }
    } catch (error) {
      onMutationErrorRef.current?.(error);
    }
  }

  if (pendingValuesRef.current) {
    await processNextRowUpdate(refs);
    return;
  }

  updateInProgressRef.current = false;

  if (isEqual(latestValuesRef.current, savedValuesRef.current)) {
    const settledValues = {
      ...rowRef.current,
      ...latestValuesRef.current,
    };
    savedValuesRef.current = settledValues;
    formikRef.current?.resetForm({ values: settledValues });
  }
};

/**
 * Sends at most one mutation at a time and coalesces pending edits to the
 * latest row values. Server refreshes are applied only when no local draft
 * or update is pending.
 */
const useSequentialRowUpdates = ({
  debouncedValues,
  enabled,
  formik,
  headers,
  onMutationError,
  row,
  updateMutation,
}) => {
  const formikRef = useRef(formik);
  const headersRef = useRef(headers);
  const latestValuesRef = useRef(formik.values);
  const lastQueuedValuesRef = useRef(row);
  const onMutationErrorRef = useRef(onMutationError);
  const pendingValuesRef = useRef(null);
  const rowRef = useRef(row);
  const savedValuesRef = useRef(row);
  const updateInProgressRef = useRef(false);
  const updateMutationRef = useRef(updateMutation);

  formikRef.current = formik;
  headersRef.current = headers;
  latestValuesRef.current = formik.values;
  onMutationErrorRef.current = onMutationError;
  rowRef.current = row;
  updateMutationRef.current = updateMutation;

  useEffect(() => {
    if (
      !enabled ||
      updateInProgressRef.current ||
      pendingValuesRef.current ||
      !isEqual(latestValuesRef.current, savedValuesRef.current)
    ) {
      return;
    }

    savedValuesRef.current = row;
    lastQueuedValuesRef.current = row;
    formikRef.current?.resetForm({ values: { ...row } });
  }, [enabled, row]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const nextChangedFields = getChangedFields(
      debouncedValues || {},
      savedValuesRef.current || {},
    );
    const pruned = sanitiseTableRowUpdate(nextChangedFields, headers);
    if (
      Object.keys(pruned).length === 0 ||
      isEqual(debouncedValues, lastQueuedValuesRef.current)
    ) {
      return;
    }

    pendingValuesRef.current = debouncedValues;
    lastQueuedValuesRef.current = debouncedValues;

    if (!updateInProgressRef.current) {
      updateInProgressRef.current = true;
      processNextRowUpdate({
        formikRef,
        headersRef,
        latestValuesRef,
        onMutationErrorRef,
        pendingValuesRef,
        rowRef,
        savedValuesRef,
        updateInProgressRef,
        updateMutationRef,
      });
    }
  }, [debouncedValues, enabled, headers]);
};

export default useSequentialRowUpdates;
